import { useTranslation } from "react-i18next";
import { FileText, Type, Crown, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useScripts } from "@/hooks/useScripts";
import { useProfile } from "@/hooks/useProfile";
import { useScriptLimits } from "@/hooks/useScriptLimits";

export function StatsCards() {
  const { t } = useTranslation();
  const { scripts } = useScripts();
  const { isTrialing, getTrialDaysLeft, getSubscriptionTier } = useProfile();
  const { formatLimit, getScriptLimit, getScriptUsagePercentage } = useScriptLimits();

  const totalWords = scripts.reduce((sum, script) => sum + script.word_count, 0);
  const tier = getSubscriptionTier();
  const trialDays = getTrialDaysLeft();
  const usagePercentage = getScriptUsagePercentage();

  const stats = [
    {
      title: t("dashboard.stats.totalScripts"),
      value: `${scripts.length}`,
      subValue: ` / ${formatLimit(getScriptLimit())}`,
      icon: FileText,
      color: "bg-card-yellow",
    },
    {
      title: t("dashboard.stats.totalWords"),
      value: totalWords.toLocaleString(),
      icon: Type,
      color: "bg-card-cyan",
    },
    {
      title: t("dashboard.stats.subscription"),
      value: isTrialing() ? `${trialDays} ${t("dashboard.stats.daysLeft")}` : t(`dashboard.tiers.${tier}`),
      icon: Crown,
      color: "bg-card-pink",
      badge: isTrialing() ? t("dashboard.stats.trial") : null,
    },
    {
      title: t("dashboard.stats.usage"),
      value: `${usagePercentage}%`,
      icon: TrendingUp,
      color: "bg-card-blue",
      progress: usagePercentage,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <Card key={index} className={`${stat.color} border-2 border-black shadow-[4px_4px_0_#1A1A1A] transition-all hover:translate-y-[-2px]`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-x-4">
              <div className="truncate text-sm font-bold text-black/70">
                {stat.title}
              </div>
              <stat.icon className="h-5 w-5 text-black/70" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-black tracking-tight whitespace-nowrap">
                {stat.value}
              </span>
              {stat.subValue && (
                <span className="text-sm font-medium text-foreground/70">
                  {stat.subValue}
                </span>
              )}
              {stat.badge && (
                <Badge variant="outline" className="border-foreground/50 text-foreground">
                  {stat.badge}
                </Badge>
              )}
            </div>
            {stat.progress !== undefined && (
              <Progress value={stat.progress} className="mt-4 h-2 border border-foreground/20" />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
